/**
 * The console losing power, and getting it back.
 *
 * In fiction a jump is flown on mechanical instruments, because nothing
 * electrical survives one. Everything the ship shows you goes dark on the way
 * in and comes back on the far side — and the same thing happens on departure,
 * because a ship coming off a pad is bringing its systems up.
 *
 * ── It is also covering a seam ──────────────────────────────────────────────
 *
 * A crossing rebuilds the ship's whole configuration — warp off, gear up,
 * thruster set reset, module stowed — and watching five switches flip at once
 * looked like a fault rather than an arrival. Behind a dark console it is
 * something that happened while you could not see it, which is both better to
 * look at and a truer account of what the ship did.
 *
 * ── Per instrument, not per screen ──────────────────────────────────────────
 *
 * Each panel collapses into *its own* middle. One wrapper over the whole HUD
 * pinched everything toward the centre of the screen — which is empty — so the
 * console appeared to be sucked sideways into nothing rather than switching
 * off. They are separate instruments and they fail as separate instruments.
 *
 * That is why this is a hook and a style rather than a wrapper component: the
 * panels already carry their own absolute positioning, and a style merges into
 * that without adding a box that would have to be positioned all over again.
 *
 * ── Why it collapses rather than fades ──────────────────────────────────────
 *
 * A fade reads as a screen effect. This is meant to read as *hardware*, so it
 * does what a cathode tube does when the power is cut: a moment of uneven
 * flicker while it fails, then the picture squashes into a bright line and the
 * line pinches out to nothing.
 *
 * The flicker intervals are deliberately uneven and do not divide evenly,
 * because anything regular reads as an animation rather than as a failure.
 */

import { useEffect, useRef, useState } from 'react';

export interface Power {
  /** Brightness, and near zero on a dead frame of the flicker. */
  lit: number;
  /** Vertical squash. Goes to a line before the width does. */
  height: number;
  /** Horizontal pinch. The last thing to go, and the first to come back. */
  width: number;
}

/** Whether anything is worth drawing at all. */
export function powerVisible(power: Power): boolean {
  return power.width > 0 && power.height > 0;
}

/**
 * The transform an instrument wears while the power is going.
 *
 * Merged into a panel's own style, so it collapses around its own centre rather
 * than around the middle of the screen.
 */
export function powerStyle(power: Power): React.CSSProperties {
  return {
    transform: `scaleX(${power.width}) scaleY(${power.height})`,
    transformOrigin: 'center center',
    // Only while collapsing. A transition on the flicker would smooth out the
    // very thing that makes it read as a failure.
    transition: power.height < 1 || power.width < 1 ? 'transform 90ms ease-out' : undefined,
    opacity: Math.min(1, power.lit),
    filter: power.lit > 1 ? `brightness(${power.lit})` : undefined,
  };
}

/** Uneven on purpose — see above. Seconds from the start of the transition. */
const FLICKER_OUT = [0.0, 0.07, 0.13, 0.19, 0.27, 0.33];
const FLICKER_IN = [0.0, 0.09, 0.15, 0.24, 0.31, 0.4];

/** How long the tube takes to collapse, and to open back up. */
const COLLAPSE = 0.34;
const EXPAND = 0.42;

const ON: Power = { lit: 1, height: 1, width: 1 };
const OFF: Power = { lit: 0, height: 0, width: 0 };

/**
 * Run the tube up or down, and report where it has got to.
 *
 * One call drives every instrument, so they fail together and come back
 * together — separately positioned, but not separately timed.
 */
export function usePower(powered: boolean): Power {
  const [power, setPower] = useState<Power>(powered ? ON : OFF);
  const settled = useRef(powered);

  useEffect(() => {
    if (settled.current === powered) return;
    settled.current = powered;

    const timers: number[] = [];
    const at = (seconds: number, state: Power) =>
      timers.push(window.setTimeout(() => setPower(state), seconds * 1000));

    if (!powered) {
      // Guttering: alternate frames dead, at intervals that do not scan.
      FLICKER_OUT.forEach((when, index) =>
        at(when, index % 2 === 0 ? ON : { lit: 0.15, height: 1, width: 1 }),
      );

      const failed = FLICKER_OUT[FLICKER_OUT.length - 1]!;

      // Then the tube goes: squashed to a bright line, then pinched out. The
      // line is *brighter* than the picture was, which is the detail that makes
      // it read as a tube rather than as a window closing.
      at(failed + 0.04, { lit: 1.9, height: 0.012, width: 1 });
      at(failed + 0.04 + COLLAPSE * 0.45, { lit: 2.2, height: 0.012, width: 0.5 });
      at(failed + COLLAPSE, OFF);
      return () => {
        for (const timer of timers) window.clearTimeout(timer);
      };
    }

    // Coming up runs it backwards: a point, a line, then the picture — with the
    // flicker *after* it opens, because a tube settles once it is already lit.
    at(0, { lit: 2.2, height: 0.012, width: 0.12 });
    at(EXPAND * 0.3, { lit: 1.9, height: 0.012, width: 1 });
    at(EXPAND * 0.6, { lit: 1.3, height: 1, width: 1 });

    FLICKER_IN.forEach((when, index) =>
      at(EXPAND * 0.6 + when, index % 2 === 0 ? { lit: 0.35, height: 1, width: 1 } : ON),
    );
    at(EXPAND * 0.6 + FLICKER_IN[FLICKER_IN.length - 1]! + 0.06, ON);

    return () => {
      for (const timer of timers) window.clearTimeout(timer);
    };
  }, [powered]);

  return power;
}

/*
 * ── Reaching the panels that are not in the cockpit ─────────────────────────
 *
 * The blackout is `FlightView`'s: it holds the crossing, it calls `usePower`,
 * and it hands the resulting style to each instrument it draws. But a lesson
 * card is drawn by `App` — deliberately, because a lesson belongs to the account
 * rather than to whichever view raised it, and one fired on the way into a berth
 * should survive arriving.
 *
 * Which left it lit and full size over a dead cockpit during a jump, along with
 * anything else drawn outside the flight tree. Threading a prop down would mean
 * `App` holding a copy of the flight's power state and passing it through every
 * view; a signal one component reports and any other can read is the smaller
 * thing, and it matches how the music director is already reached from
 * everywhere.
 *
 * It carries the whole `Power` rather than a lit/dark flag so those panels
 * *collapse* with the instruments instead of blinking out beside them. The
 * flicker is the point of the effect; a card that merely vanished at the end of
 * it would read as a fault in the game rather than in the ship.
 *
 * The default is lit, and `FlightView` restores it on the way out — nothing
 * outside a crossing should ever have to know this exists.
 */

let reported: Power = ON;
const watchers = new Set<(power: Power) => void>();

/** Report the cockpit's power. Called by the view that owns the blackout. */
export function reportPower(power: Power): void {
  if (
    reported.lit === power.lit &&
    reported.width === power.width &&
    reported.height === power.height
  ) {
    return;
  }

  reported = power;
  for (const watcher of watchers) watcher(power);
}

/** The cockpit's power, for a panel drawn outside it. */
export function useReportedPower(): Power {
  const [power, setPower] = useState(reported);

  useEffect(() => {
    // Re-read on subscribe: a report may have landed between the first render
    // and this effect, which during a crossing is exactly when it does.
    setPower(reported);
    watchers.add(setPower);
    return () => {
      watchers.delete(setPower);
    };
  }, []);

  return power;
}
